import React, { useEffect, useState } from 'react'
import "./hero.scss"
import { useTranslation } from 'react-i18next'
import { Select, Form, Input, DatePicker, InputNumber, message, Modal, Button, Alert } from 'antd'
import dayjs from 'dayjs'
import { AppDispatch, RootState } from '../../../store'
import { useDispatch, useSelector } from 'react-redux'
import APPLICATIONS from "../../data/Applications"
import { createReservation, idleReservationStatus } from '../../calendar/calendarEventSlice'
import { rangeOverlapsWorks } from '../../../utils/worksPeriod'

const { RangePicker } = DatePicker

interface BookingValues {
  dates: [dayjs.Dayjs, dayjs.Dayjs]
  typeService: string
  occupants: number
}

interface ContactValues {
  fullname: string
  email: string
}

const Hero = () => {
  const { t } = useTranslation('home')
  const nameApplication = APPLICATIONS[0]?.name
  const applicationId = APPLICATIONS[0]?.id
  const dispatch = useDispatch<AppDispatch>()
  const [successModalVisible, setSuccessModalVisible] = useState(false)
  const [contactModalVisible, setContactModalVisible] = useState(false)
  const [bookingForm] = Form.useForm<BookingValues>()
  const [contactForm] = Form.useForm<ContactValues>()
  const reservationStatus = useSelector((state: RootState) => state.calendar)
  const [submitting, setSubmitting] = useState(false)
  const [datesOverlapWorks, setDatesOverlapWorks] = useState(false)

  const openContactModal = async () => {
    try {
      const values = await bookingForm.validateFields()
      const [startDate, endDate] = values.dates
      setDatesOverlapWorks(rangeOverlapsWorks(startDate, endDate))
      setContactModalVisible(true)
    } catch {
      // validation errors already surfaced by antd on the booking fields
    }
  }

  const submitReservation = (contactValues: ContactValues) => {
    if (submitting) return
    setSubmitting(true)

    const { dates, typeService, occupants } = bookingForm.getFieldsValue()

    if (nameApplication && applicationId) {
      const [startDate, endDate] = dates
      const newNote = contactValues.fullname + " voudrait réserver " + nameApplication + " pour la période de " + startDate.format('DD-MM-YYYY [à] HH:mm') + " à " + endDate.format('DD-MM-YYYY [à] HH:mm') + " avec motif " + typeService + " pour " + occupants + " personne(s), son email est: " + contactValues.email.trim()
      const contactData = {
        name: contactValues.fullname,
        email: contactValues.email.trim(),
        message: newNote,
        website: nameApplication,
      }
      const event = {
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
        roomId: applicationId,
        occupants: occupants,
        type: 3,
        note: newNote,
      }
      dispatch(createReservation({ contactData, event }))
    }
  }

  useEffect(() => {
    if (reservationStatus.demandContactStatus === 'success' && reservationStatus.fetchCreateEventStatus === 'success') {
      setContactModalVisible(false)
      setSuccessModalVisible(true)
      message.success(t('Your demand of reservation has been successfully sent.'))
      bookingForm.resetFields()
      contactForm.resetFields()
      setSubmitting(false)
    } else if (reservationStatus.demandContactStatus === 'error' || reservationStatus.fetchCreateEventStatus === 'error') {
      message.error(t('An error occurred.', { ns: 'common' }))
      setSubmitting(false)
    }
    return () => {
      dispatch(idleReservationStatus())
    }
  }, [reservationStatus, t, bookingForm, contactForm, dispatch])

  const scrollToRooms = (e: React.MouseEvent) => {
    e.preventDefault()
    document.getElementById('rooms')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      <section className='home-hero'>
        <div className='hero__bg' />
        <div className='hero__overlay' />
        <div className='hero__content'>
          <p className='hero__eyebrow'>{t('Sologne — Val de Loire')}</p>
          <h1 className='hero__title'>{t('Le Corvier Castle', { ns: 'common' })}</h1>
          <p className='hero__subtitle'>{t('An exceptional residence in the heart of Sologne')}</p>
          <p className='hero__intro'>{t('Discover our magnificent castle, where history meets luxury in the heart of French nature.')}</p>
          <span className='hero__rule' />
          <a href='#rooms' className='hero__cta' onClick={scrollToRooms}>{t('Discover the rooms')}</a>
        </div>

        <div className='booking-bar'>
          <Form form={bookingForm} className='booking-bar__form' layout='vertical' disabled={submitting}>
            <Form.Item
              name="dates"
              className='booking-bar__field booking-bar__field--dates'
              label={t('Stay dates')}
              rules={[{ required: true, message: t('Please input dates') || "" }]}
            >
              <RangePicker
                showTime
                format={t('Date format') as string}
                separator={t('Date range separator') as string}
                inputReadOnly
                suffixIcon={null}
                placeholder={[t('Start date'), t('End date')]}
                disabledDate={(current) =>
                  current && dayjs(current).isBefore(dayjs().subtract(1, 'day'))
                }
                disabled={submitting}
              />
            </Form.Item>
            <span className='booking-bar__divider' />
            <Form.Item name="typeService" className='booking-bar__field' label={t('Type of stay')}>
              <Select
                defaultOpen={false}
                suffixIcon={null}
                placeholder={t('Type of stay')}
                disabled={submitting}
              >
                <Select.Option value={'Louer chateau entier'}>
                  {t('Castle rental')}
                </Select.Option>
                <Select.Option value={'Camping'}>
                  {t('Camping')}
                </Select.Option>
                <Select.Option value={'Mariage'}>
                  {t('Marriage')}
                </Select.Option>
                <Select.Option value={'Autre'}>
                  {t('Other')}
                </Select.Option>
              </Select>
            </Form.Item>
            <span className='booking-bar__divider' />
            <Form.Item name="occupants" className='booking-bar__field' label={t('Guests')} initialValue={10}>
              <InputNumber
                min={10}
                max={60}
                controls={false}
                addonAfter={t('pers', { ns: 'common' })}
                disabled={submitting}
              />
            </Form.Item>
            <button
              type='button'
              className='booking-bar__cta'
              onClick={openContactModal}
              disabled={submitting || reservationStatus.demandContactStatus === 'loading'}
            >
              {t('Check availability')}
            </button>
          </Form>
        </div>
      </section>

      <Modal
        open={contactModalVisible}
        onCancel={() => setContactModalVisible(false)}
        footer={null}
        title={t('Ask us for reservation')}
        className='booking-contact-modal'
      >
        {datesOverlapWorks && (
          <Alert
            type='warning'
            showIcon
            className='works-overlap-alert'
            message={t('These dates overlap with our renovation works period', { ns: 'common' })}
            description={t('You can still send us your request, we will get back to you to confirm availability.', { ns: 'common' })}
          />
        )}
        <Form
          form={contactForm}
          layout='vertical'
          onFinish={submitReservation}
          disabled={submitting}
        >
          <Form.Item name="fullname" label={t('Your name')} rules={[{ required: true, message: t('Please input your name') as string }]}>
            <Input placeholder={t('Your name') as string} disabled={submitting} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('Your email')}
            rules={[
              { required: true, message: t('Please enter your email') as string },
              { type: 'email', message: t('Please enter a valid email address', { ns: 'common' }) as string, validateTrigger: 'onSubmit' }
            ]}
          >
            <Input placeholder={t('Your email') as string} disabled={submitting} />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={submitting || reservationStatus.demandContactStatus === 'loading'}
            disabled={submitting || reservationStatus.demandContactStatus === 'loading'}
          >
            {t('Send')}
          </Button>
        </Form>
      </Modal>

      <Modal
        open={successModalVisible}
        onOk={() => setSuccessModalVisible(false)}
        onCancel={() => setSuccessModalVisible(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <p>{t('Your request has been submitted successfully, we received your email and we\'ll answer you within a few days', { ns: 'common' })}</p>
      </Modal>
    </>
  )
}

export default Hero
